import type { BookingStatus } from '@coachapp/shared';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorAlert from '../components/ErrorAlert';
import { ScreenHeader } from '../components/layout/ScreenHeader';
import TransitionLink from '../components/TransitionLink';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { ANALYTICS_STATS } from '../lib/analytics-stats';
import { getApiErrorMessage } from '../lib/api-error';
import { computeDashboardAnalytics } from '../lib/dashboard-analytics';
import {
  useGetBookingsQuery,
  useGetClientsQuery,
  useGetFunnelQuery,
  useGetRetentionQuery,
  useGetTrainingPlansQuery,
} from '../store/coachApi';

const STATUS_COLORS: Record<BookingStatus, string> = {
  PENDING: 'bg-amber-500',
  CONFIRMED: 'bg-primary',
  COMPLETED: 'bg-emerald-500',
  CANCELLED: 'bg-slate-300',
};

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useTransitionNavigate();
  const clientsQuery = useGetClientsQuery();
  const bookingsQuery = useGetBookingsQuery();
  const plansQuery = useGetTrainingPlansQuery();
  const retentionQuery = useGetRetentionQuery();
  const funnelQuery = useGetFunnelQuery();

  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const bookings = useMemo(() => bookingsQuery.data ?? [], [bookingsQuery.data]);
  const plans = plansQuery.data ?? [];
  const retention = retentionQuery.data ?? null;
  const funnel = funnelQuery.data ?? null;

  const dataQueries = [
    clientsQuery,
    bookingsQuery,
    plansQuery,
    retentionQuery,
    funnelQuery,
  ];
  const loading = dataQueries.some((query) => query.isLoading);

  const fetchError = useMemo(() => {
    const err =
      clientsQuery.error ||
      bookingsQuery.error ||
      plansQuery.error ||
      retentionQuery.error ||
      funnelQuery.error;
    return err ? getApiErrorMessage(err, 'Не удалось загрузить данные') : null;
  }, [
    clientsQuery.error,
    bookingsQuery.error,
    plansQuery.error,
    retentionQuery.error,
    funnelQuery.error,
  ]);

  const refetchAll = () => {
    void clientsQuery.refetch();
    void bookingsQuery.refetch();
    void plansQuery.refetch();
    void retentionQuery.refetch();
    void funnelQuery.refetch();
  };

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const analytics = useMemo(
    () => computeDashboardAnalytics(bookings, clients, locale),
    [bookings, clients, locale],
  );

  const statsList = ANALYTICS_STATS.map((stat) => {
    let value: string | number = '—';

    switch (stat.id) {
      case 'totalClients':
        value = clients.length;
        break;
      case 'activeClients':
        value = analytics.activeClients;
        break;
      case 'plans':
        value = plans.length;
        break;
      case 'todaySessions':
        value = analytics.todaySessions;
        break;
      case 'weekSessions':
        value = analytics.weekSessions;
        break;
      case 'pending':
        value = analytics.pendingCount;
        break;
      case 'churn':
        value = retention ? `${(retention.churnRate30d * 100).toFixed(1)}%` : '—';
        break;
      case 'ltv':
        value = retention
          ? `${(retention.avgLtvCents / 100).toLocaleString(analytics.locale)} ₽`
          : '—';
        break;
      case 'conversion':
        value = funnel ? `${(funnel.conversionRate * 100).toFixed(1)}%` : '—';
        break;
    }

    return {
      ...stat,
      title: t(stat.titleKey),
      value,
    };
  });

  const funnelStages = funnel
    ? [
        { key: 'INQUIRY', count: funnel.inquiry, color: 'bg-blue-500' },
        { key: 'TRIAL', count: funnel.trial, color: 'bg-amber-500' },
        { key: 'CONVERTED', count: funnel.converted, color: 'bg-emerald-500' },
        { key: 'LOST', count: funnel.lost, color: 'bg-slate-400' },
      ]
    : [];
  const maxFunnelCount = Math.max(1, ...funnelStages.map((s) => s.count));

  const formatAtRiskReason = (reason: string) =>
    reason
      .split(',')
      .map((code) => t(`atRisk.${code.trim()}`))
      .join(' · ');

  const renderStatCard = (stat: (typeof statsList)[number]) => {
    const Icon = stat.icon;
    return (
      <TransitionLink
        key={stat.id}
        to={`/analytics/${stat.id}`}
        className="stat-card group min-w-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        aria-label={t('analyticsStat.openDetail', { title: stat.title })}
      >
        <div className="flex justify-between items-start mb-3 gap-2">
          <span className="flex-1 min-w-0 section-label leading-tight break-words">
            {stat.title}
          </span>
          <div
            className={`p-2 rounded-xl bg-gradient-to-br ${stat.accent} flex items-center justify-center shrink-0`}
          >
            <Icon size={18} />
          </div>
        </div>
        <div className="flex items-end justify-between gap-2">
          <div className="text-2xl md:text-3xl font-bold text-slate-900 tabular-nums">{stat.value}</div>
          <ArrowRight
            size={16}
            className="text-slate-300 group-hover:text-primary shrink-0 mb-1 transition-colors"
          />
        </div>
      </TransitionLink>
    );
  };

  if (loading) {
    return (
      <div className="page-container flex justify-center py-24">
        <div className="spinner h-10 w-10" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <ScreenHeader
        title={t('Аналитика')}
        subtitle={t('Следите за вашими основными показателями')}
        onBack={() => navigate('/profile')}
        backLabel={t('Назад')}
      />

      {fetchError && (
        <ErrorAlert
          message={fetchError}
          onRetry={refetchAll}
          onDismiss={() => {}}
        />
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
        {statsList.map(renderStatCard)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card">
          <h2 className="text-lg font-bold text-slate-900 mb-1">{t('Воронка потенциальных клиентов')}</h2>
          <p className="text-sm text-app-muted mb-6">{t('Распределение потенциальных клиентов по этапам')}</p>
          {funnelStages.length === 0 ? (
            <p className="text-sm text-app-muted">{t('Нет потенциальных клиентов')}</p>
          ) : (
            <div className="space-y-4">
              {funnelStages.map(({ key, count, color }) => (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="font-medium text-slate-700">{t(`leadStage.${key}`)}</span>
                    <span className="font-bold text-slate-900 tabular-nums">{count}</span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${color} transition-all duration-500`}
                      style={{ width: `${(count / maxFunnelCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <h2 className="text-lg font-bold text-slate-900 mb-1">{t('Клиенты под риском')}</h2>
          <p className="text-sm text-app-muted mb-6">{t('Клиенты, требующие внимания')}</p>
          {!retention?.atRiskClients.length ? (
            <div className="flex flex-col items-center justify-center py-8 text-app-muted">
              <AlertTriangle className="mb-3 opacity-30" size={36} />
              <p className="text-sm font-medium">{t('Нет клиентов под риском')}</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {retention.atRiskClients.map((c) => (
                <li key={c.id}>
                  <TransitionLink
                    to={`/clients/${c.id}`}
                    className="flex items-start gap-3 p-3 rounded-xl border border-app-border hover:bg-primary/5 transition-colors"
                  >
                    <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{formatAtRiskReason(c.reason)}</p>
                    </div>
                    <ArrowRight size={16} className="text-slate-400 shrink-0 mt-1" />
                  </TransitionLink>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card">
          <h2 className="text-lg font-bold text-slate-900 mb-1">{t('Активность за 7 дней')}</h2>
          <p className="text-sm text-app-muted mb-6">{t('Количество тренировок по дням')}</p>
          <div className="flex items-end justify-between gap-2 h-36">
            {analytics.last7Days.map(({ day, count }) => (
              <div key={day.toISOString()} className="flex-1 flex flex-col items-center gap-2 min-w-0">
                <span className="text-xs font-bold text-slate-700 tabular-nums">{count}</span>
                <div className="w-full flex items-end justify-center h-24 bg-slate-100/80 rounded-xl overflow-hidden">
                  <div
                    className="w-full max-w-[2rem] bg-gradient-to-t from-primary to-indigo-400 rounded-t-lg transition-all duration-500"
                    style={{ height: `${(count / analytics.maxDayCount) * 100}%`, minHeight: count > 0 ? '8px' : '0' }}
                  />
                </div>
                <span className="section-label truncate w-full text-center">
                  {day.toLocaleDateString(analytics.locale, { weekday: 'short' })}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <h2 className="text-lg font-bold text-slate-900 mb-1">{t('Распределение по статусам')}</h2>
          <p className="text-sm text-app-muted mb-6">
            {t('Всего записей')}: {bookings.length}
          </p>
          <div className="space-y-4">
            {analytics.statusCounts.map(({ status, count }) => (
              <div key={status}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium text-slate-700">{t(`status.${status}`)}</span>
                  <span className="font-bold text-slate-900 tabular-nums">{count}</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${STATUS_COLORS[status]} transition-all duration-500`}
                    style={{ width: `${(count / analytics.totalBookings) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
